import type { FilterAdjustments } from '@renderer/types/configState'

/**
 * The first stage of a filter bake, as a CSS `filter` string.
 *
 * `imageUtils.applyFilterToImage` starts every bake with these same CSS
 * functions in this same order (utils/filterUtils.ts, "Basic CSS Filters"),
 * then does its per-pixel work — temperature, shadows, grain. The CSS stage is
 * what makes a mono filter mono, so a photograph shown with it reads as the
 * filtered shot while the real bake is still running, instead of flashing up
 * in colour under a black-and-white live view.
 */
export function cssStageOf(adjustments: FilterAdjustments | undefined): string | undefined {
  if (!adjustments) return undefined
  const parts: string[] = []
  // Same functions, same order, same neutral values as filterUtils'
  // "Basic CSS Filters" stage — including grayscale, which is how some mono
  // filters are built, and without which they flashed up in colour.
  const a = adjustments as FilterAdjustments & {
    grayscale?: number
    hueRotate?: number
    invert?: number
  }
  if (a.contrast !== undefined && a.contrast !== 100) parts.push(`contrast(${a.contrast}%)`)
  if (a.brightness !== undefined && a.brightness !== 100) parts.push(`brightness(${a.brightness}%)`)
  if (a.saturation !== undefined && a.saturation !== 100) parts.push(`saturate(${a.saturation}%)`)
  if (a.sepia !== undefined && a.sepia !== 0) parts.push(`sepia(${a.sepia}%)`)
  if (a.blur !== undefined && a.blur !== 0) parts.push(`blur(${a.blur}px)`)
  if (a.grayscale !== undefined && a.grayscale !== 0) parts.push(`grayscale(${a.grayscale}%)`)
  if (a.hueRotate !== undefined && a.hueRotate !== 0) parts.push(`hue-rotate(${a.hueRotate}deg)`)
  if (a.invert !== undefined && a.invert !== 0) parts.push(`invert(${a.invert}%)`)
  return parts.length ? parts.join(' ') : undefined
}
